import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const FAQS = [
  {
    q: "Why only email for change requests? Isn't that slow?",
    a: "It's slow on purpose. Real-time chat creates anxiety and shallow thinking — for both sides. Email forces the request to be written down, considered, and prioritized. In practice, our clients ship faster because they request better.",
  },
  {
    q: "How fast are you really?",
    a: "First working build is typically within 48 hours. Most change requests on the Standard plan ship within 1–2 business days. Priority customers see same-day turnaround for critical paths. We're closer to vibe-coding speed than to traditional development — without the fragility.",
  },
  {
    q: "Is this secure enough for regulated data?",
    a: "Yes. We operate with SOC 2 Type II-aligned controls, AES-256 encryption at rest, TLS 1.3 in transit, and end-to-end encryption where the platform supports it. Custom security reviews and DPAs are part of the Priority plan.",
  },
  {
    q: "What if I need a real app, not just a chat interface?",
    a: "Sometimes a real app is the right answer. We'll tell you when it is. But for a surprising amount of business logic, a thoughtful conversation interface inside an app your users already trust beats a new download nobody opens.",
  },
  {
    q: "Which platform should we start with?",
    a: "It depends entirely on where your customers already are. In the US: iMessage. International consumer: WhatsApp. Power-user communities: Telegram. B2B internal tools: Slack. We figure this out in the consultation, not in a sales pitch.",
  },
  {
    q: "What happens if I cancel?",
    a: "Your system keeps running. We hand over architecture docs, credentials, and a clean export. No hostage data, no exit fees. The relationship is monthly because we want to earn it monthly.",
  },
  {
    q: "Do you sign NDAs and DPAs?",
    a: "Yes, both — standard practice. We'll send our templates or sign yours.",
  },
  {
    q: "What does 'invisible' actually mean here?",
    a: "It means there is no new app for your customer to download, no new login to remember, no new habit to form. The software lives entirely inside the messaging app they already check every twelve minutes. That's the unfair advantage.",
  },
];

export default function FAQ() {
  return (
    <section
      id="faq"
      data-testid="faq-section"
      className="relative py-24 md:py-32 border-b border-white/10"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="flex items-center gap-3 mb-12">
          <span className="text-xs uppercase tracking-[0.22em] text-zinc-500 font-mono">
            §08 · Things you'll probably ask
          </span>
        </div>

        <div className="grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl tracking-tight font-light leading-snug sticky top-28">
              Quiet
              <br />
              <span className="text-zinc-500">answers.</span>
            </h2>
          </div>

          <div className="lg:col-span-8">
            <Accordion type="single" collapsible className="w-full">
              {FAQS.map((f, i) => (
                <AccordionItem
                  key={i}
                  value={`item-${i}`}
                  data-testid={`faq-item-${i}`}
                  className="border-b border-white/10 last:border-b-0"
                >
                  <AccordionTrigger
                    data-testid={`faq-trigger-${i}`}
                    className="text-left font-heading text-lg md:text-xl font-light tracking-tight text-white hover:text-yellow-400 hover:no-underline py-6"
                  >
                    <span className="flex items-baseline gap-4">
                      <span className="font-mono text-[11px] text-zinc-600 tracking-[0.22em]">
                        {String(i + 1).padStart(2, "0")}
                      </span>
                      {f.q}
                    </span>
                  </AccordionTrigger>
                  <AccordionContent className="pb-6 pl-10 text-base text-zinc-400 font-light leading-relaxed max-w-2xl">
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </div>
    </section>
  );
}
